import { AfterViewInit, Component, ElementRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { IonContent, ModalController } from '@ionic/angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DishDto } from '@timechef/core/api';
import { isNullOrUndefined } from '@timechef/core/utils/js-utils';
import { RestaurantsRepository, RestaurantsService } from '@timechef/features/restaurants/state';
import { CartFormula, CartFormulaDish, SelectableFormula } from '@timechef/features/restaurants/state/restaurants.models';
import { BehaviorSubject } from 'rxjs';
@UntilDestroy()
@Component({
  selector: 'app-formula-modal',
  templateUrl: './formula-modal.component.html',
  styleUrls: ['./formula-modal.component.scss'],
  standalone: false,
})
export class FormulaModalComponent implements OnInit, AfterViewInit {
  @Input() formula!: SelectableFormula;
  @Input() restaurantId!: number;
  @Input() date!: Date;
  @Input() cartFormula?: CartFormula;

  form?: FormGroup;
  showWarning = false;

  @ViewChild('title') titleEl?: ElementRef;
  @ViewChild('formulaContainer', { read: ElementRef }) formulaContainerEl?: ElementRef;
  @ViewChild(IonContent) ionContent?: IonContent;

  showDataInHeader$ = new BehaviorSubject(false);
  sectionBreakpoints: number[] = [];

  private restaurantsService: RestaurantsService;

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private restaurantsRepo: RestaurantsRepository,
    private modalCtrl: ModalController,
  ) {
    this.restaurantsService = injector.get(RestaurantsService);
  }

  ngOnInit() {
    this.form = this.fb.group({
      sections: this.fb.array([]), // FormArray pour les sections
    });

    this.formula.sections.forEach((s) => {
      // // order limit date
      // s.dishes = s.dishes.map()

      const dishes = this.fb.array(
        s.dishes.map((d) => {
          const cartDish = this.cartFormula?.dishes.find((cd) => cd.sectionId === s.id && cd.dishId === d.id);
          return this.fb.group({
            id: d.id,
            label: d.label,
            quantity: cartDish ? cartDish.quantity : 0,
          });
        }),
      );

      const sectionGrp = this.fb.group({
        ...s,
        id: s.id,
        dishes: dishes, // FormArray pour les plats
      });

      dishes.controls.forEach((curDish) => {
        curDish.valueChanges.subscribe((value) => {
          const otherControls = dishes.controls.filter((dish) => dish.value.id !== curDish.value.id);
          const { maxQuantity } = sectionGrp.value;
          // sélection unique, on déselectionne les autres plats
          if (value.quantity && maxQuantity === 1) {
            otherControls.forEach((dish) => dish.patchValue({ quantity: 0 }));
          } else if (!isNullOrUndefined(maxQuantity) && maxQuantity >= 2) {
            const total = dishes.controls.reduce((sum, c) => sum + (c.value.quantity ?? 0), 0);
            const shouldDisable = total >= maxQuantity;

            otherControls
              .filter((x) => !x.value.quantity)
              .forEach((x) => (shouldDisable ? x.disable({ emitEvent: false, onlySelf: true }) : x.enable({ emitEvent: false, onlySelf: true })));
          }
        });
      });

      this.sections.push(sectionGrp);
    });

    if (!this.formula?.imageUrl) {
      setTimeout(() => {
        this.showDataInHeader$.next(true);
      }, 0);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.computeBreakpoints();
    }, 50);
  }

  computeBreakpoints() {
    const sectionEl = this.formulaContainerEl?.nativeElement?.querySelector('.formula-detail-inner');
    if (sectionEl) {
      const titleHeight = this.titleEl?.nativeElement?.offsetHeight > 26 ? 88 : 68;
      const sections = sectionEl.querySelectorAll('.section-title-container');
      const sectionBreakpoints: number[] = [];
      sections.forEach((sectionEl: any) => {
        sectionBreakpoints.push(sectionEl.offsetTop - titleHeight);
      });
      this.sectionBreakpoints = sectionBreakpoints;
    }
  }

  async addOrUpdateFormula() {
    let isFormValid = true;

    for (let index = 0; index < this.form!.value.sections.length; index++) {
      const section = this.form!.value.sections[index];
      if (section.isRequired && !this.dishCountIsValid(index)) {
        this.showWarning = true;
        this.scrollToSectionByIdx(index);
        isFormValid = false;
        return;
      }
    }

    if (isFormValid) {
      const dishes: CartFormulaDish[] = [];
      this.form!.value.sections.forEach((s: any) => {
        s.dishes.forEach((d: any) => {
          if (d.quantity) {
            dishes.push({
              dishId: d.id,
              dishLabel: d.label,
              quantity: d.quantity ? 1 : 0,
              sectionId: s.id,
            });
          }
        });
      });

      const cartFormula: CartFormula = {
        formulaId: this.formula.id,
        tempFormulaGuid: this.cartFormula?.tempFormulaGuid ?? '',
        formulaLabel: this.formula.label,
        quantity: 1,
        price: this.formula.price,
        dishes,
      };

      this.restaurantsService.addOrUpdateFormulaInCart(cartFormula);
      await this.modalCtrl.dismiss();
      if (!this.cartFormula) await this.restaurantsService.openCartModal();
    }
  }

  async deleteFormula() {
    if (!this.cartFormula) return;

    this.restaurantsService.deleteFormulaFromCart(this.cartFormula);
    await this.modalCtrl.dismiss();
  }

  onScroll(ev: any) {
    if (this.formula?.imageUrl) {
      const y = ev.detail.scrollTop;
      this.showDataInHeader$.next(y >= window.innerHeight * 0.23);
    }
  }

  get sections(): FormArray {
    return this.form!.get('sections') as FormArray;
  }

  getDishes(sectionIndex: number): FormArray {
    return this.sections.at(sectionIndex).get('dishes') as FormArray;
  }

  dishCountIsValid(sectionIndex: number): boolean {
    const section = this.sections.at(sectionIndex) as FormGroup;
    const dishes = this.getDishes(sectionIndex);
    const minQuantity = section.value.minQuantity || 1;
    const maxQuantity = section.value.maxQuantity || 1;

    const totalQuantity = dishes.controls.reduce((sum, control) => {
      const dish = control as FormGroup;
      return sum + (dish.get('quantity')?.value || 0);
    }, 0);

    return maxQuantity >= totalQuantity && totalQuantity >= minQuantity;
  }

  onCheckboxClick(event: MouseEvent) {
    event.stopPropagation();
  }

  async onDishCardClick(sectionId: number, dishId: number) {
    let dish: DishDto | undefined;
    const section = this.formula.sections.find((section) => section.id === sectionId);
    if (section) {
      dish = section.dishes.find((dish) => dish.id === dishId);
    }
    if (dish)
      await this.restaurantsService.openDishDetailsModal(this.restaurantId, dish, this.date, {
        fromCart: false,
        allowQuantityPicker: false,
      });
  }

  private scrollToSectionByIdx = (idx: number) => {
    return this.ionContent?.scrollToPoint(null, this.sectionBreakpoints[idx], ANIMATION_DURATION);
  };

  async dismissModal() {
    await this.modalCtrl.dismiss();
  }
}

const ANIMATION_DURATION = 150;
